import React, { useRef, useState, useCallback, useEffect } from "react";

import {
  Box,
  IconButton,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CropPortraitIcon from "@mui/icons-material/CropPortrait";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import Crop169Icon from "@mui/icons-material/Crop169";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import MusicOffIcon from "@mui/icons-material/MusicOff";

import AudiogramRenderer from "../../Audiograms/components/templates/AudiogramRenderer";
import { AudiogramEditorState, AspectRatio } from "../../Audiograms/types";
import { EDITOR_TOKENS } from "../editorTokens";

interface LivePreviewProps {
  state: AudiogramEditorState;
  audioUrl?: string;
  onAspectRatioChange: (ratio: AspectRatio) => void;
  onTimeUpdate?: (timeInSeconds: number) => void;
}

const RATIO_BUTTONS: {
  value: AspectRatio;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    value: "9:16",
    label: "9:16",
    icon: <CropPortraitIcon sx={{ fontSize: 18 }} />,
  },
  {
    value: "1:1",
    label: "1:1",
    icon: <CropSquareIcon sx={{ fontSize: 18 }} />,
  },
  {
    value: "16:9",
    label: "16:9",
    icon: <Crop169Icon sx={{ fontSize: 18 }} />,
  },
];

// The canvas height is derived from its width via the CSS aspect-ratio, so we
// cap the *width* by both a comfortable maximum and the vertical space left
// after the page chrome (header + action bar + ratio toggle + player +
// padding ≈ 290px). This guarantees the full audiogram fits without cropping.
const PREVIEW_WIDTHS: Record<AspectRatio, string> = {
  "9:16": "min(320px, calc((100vh - 290px) * 9 / 16))",
  "1:1": "min(420px, calc(100vh - 290px))",
  "16:9": "min(720px, calc((100vh - 290px) * 16 / 9))",
};

const WAVE_BUCKETS = 56;
const SAMPLE_INTERVAL_MS = 45;

const fmt = (s: number): string => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }
  return `${m}:${String(sec).padStart(2, "0")}`;
};

// Decorative fallback when real amplitude data isn't available (e.g. the audio
// is cross-origin without CORS, so the analyser only returns silence). Still
// driven by the playhead so the waveform visibly moves while playing.
function computeSynthLevels(time: number, count = WAVE_BUCKETS): number[] {
  const out: number[] = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const a = 0.5 + 0.5 * Math.sin(time * 6 + i * 0.45);
    const b = 0.5 + 0.5 * Math.sin(time * 11 + i * 0.9 + 1.3);
    const envelope = 0.5 + 0.5 * Math.sin((i / count) * Math.PI);
    out[i] = 0.12 + 0.72 * (0.6 * a + 0.4 * b) * envelope;
  }
  return out;
}

export default function LivePreview({
  state,
  audioUrl,
  onAspectRatioChange,
  onTimeUpdate,
}: LivePreviewProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [levels, setLevels] = useState<number[]>([]);

  // Web Audio graph for the live waveform.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const freqDataRef = useRef<Uint8Array | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastSampleRef = useRef<number>(0);

  const startSec = state.startTime / 1000;
  const endSec = state.endTime / 1000;
  const hasClipRange = endSec > startSec;
  const clipStart = hasClipRange ? startSec : 0;
  const clipEnd = hasClipRange ? endSec : duration;
  const clipDuration = clipEnd - clipStart;

  const animate = useCallback(() => {
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - lastSampleRef.current >= SAMPLE_INTERVAL_MS) {
      lastSampleRef.current = now;
      let next: number[] | null = null;
      const analyser = analyserRef.current;
      const data = freqDataRef.current;
      if (analyser && data) {
        analyser.getByteFrequencyData(data);
        const usable = Math.floor(data.length * 0.7);
        const step = Math.max(1, Math.floor(usable / WAVE_BUCKETS));
        const tmp: number[] = new Array(WAVE_BUCKETS);
        let sum = 0;
        for (let i = 0; i < WAVE_BUCKETS; i += 1) {
          const v = data[i * step] ?? 0;
          tmp[i] = v / 255;
          sum += v;
        }
        if (sum > 0) next = tmp;
      }
      if (!next) {
        next = computeSynthLevels(audioRef.current?.currentTime ?? 0);
      }
      setLevels(next);
    }
    rafRef.current = requestAnimationFrame(animate);
  }, []);

  const startLevels = useCallback(() => {
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(animate);
    }
  }, [animate]);

  const stopLevels = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setLevels([]);
  }, []);

  const setupAudioGraph = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || audioCtxRef.current) return;
    try {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      const source = ctx.createMediaElementSource(audio);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      sourceRef.current = source;
      freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
    } catch {
      // Analyser unavailable (CORS / source already captured): fall back to
      // the synthesized, playhead-driven waveform.
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsReady(true);
      if (clipStart > 0) {
        audio.currentTime = clipStart;
        setCurrentTime(clipStart);
      }
    };

    const onTimeUpdateEvent = () => {
      const t = audio.currentTime;
      setCurrentTime(t);
      onTimeUpdate?.(t);

      if (clipEnd > 0 && t >= clipEnd) {
        audio.pause();
        setIsPlaying(false);
        stopLevels();
        audio.currentTime = clipStart;
        setCurrentTime(clipStart);
      }
    };

    const onEnded = () => {
      setIsPlaying(false);
      stopLevels();
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdateEvent);
    audio.addEventListener("ended", onEnded);

    if (audio.readyState >= 1) {
      onLoadedMetadata();
    }

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdateEvent);
      audio.removeEventListener("ended", onEnded);
    };
  }, [audioUrl, clipStart, clipEnd, onTimeUpdate, stopLevels]);

  // Tear down the audio graph + animation loop on unmount.
  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
      }
    },
    [],
  );

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      stopLevels();
    } else {
      if (clipEnd > 0 && audio.currentTime >= clipEnd) {
        audio.currentTime = clipStart;
        setCurrentTime(clipStart);
      }
      setupAudioGraph();
      audioCtxRef.current?.resume?.().catch(() => {});
      audio.play().catch(() => {});
      setIsPlaying(true);
      startLevels();
    }
  }, [isPlaying, clipEnd, clipStart, setupAudioGraph, startLevels, stopLevels]);

  const handleSeekCommit = useCallback(
    (_: React.SyntheticEvent | Event, value: number | number[]) => {
      const audio = audioRef.current;
      if (!audio) return;
      const t = value as number;
      audio.currentTime = t;
      setCurrentTime(t);
      onTimeUpdate?.(t);
    },
    [onTimeUpdate],
  );

  const elapsed = currentTime - clipStart;

  return (
    <Box
      sx={{
        flex: 1,
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: { xs: 2, md: 3 },
      }}
    >
      {/* `margin: auto` centers the stage vertically when there is spare room
          but, unlike `justifyContent: center` on a scroll container, never
          clips the top of the content once it overflows. */}
      <Box
        sx={{
          m: "auto",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Aspect ratio toggle */}
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={state.aspectRatio}
            exclusive
            aria-label="Aspect ratio"
            onChange={(_, val) => {
              if (val) onAspectRatioChange(val as AspectRatio);
            }}
            size="small"
            sx={{
              backgroundColor: EDITOR_TOKENS.panelSurface,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "10px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "8px",
                px: 1.5,
                py: 0.5,
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "none",
                gap: 0.5,
                "&:focus-visible": {
                  outline: `2px solid ${EDITOR_TOKENS.accent}`,
                  outlineOffset: "-2px",
                },
                "&.Mui-selected": {
                  backgroundColor: EDITOR_TOKENS.accentSoft,
                  color: EDITOR_TOKENS.accent,
                },
              },
            }}
          >
            {RATIO_BUTTONS.map((btn) => (
              <ToggleButton key={btn.value} value={btn.value}>
                {btn.icon}
                <Typography sx={{ fontSize: "11px", fontWeight: 600 }}>
                  {btn.label}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Preview render — framed canvas */}
        <Box
          sx={{
            width: PREVIEW_WIDTHS[state.aspectRatio],
            maxWidth: "100%",
            borderRadius: EDITOR_TOKENS.frameRadius,
            overflow: "hidden",
            backgroundColor: EDITOR_TOKENS.panelSurface,
            p: 0.75,
            border: "1px solid",
            borderColor: "divider",
            boxShadow:
              "0 1px 2px rgba(15,23,42,0.06), 0 18px 40px -12px rgba(15,23,42,0.28)",
          }}
        >
          <Box
            sx={{
              borderRadius: "13px",
              overflow: "hidden",
            }}
          >
            <AudiogramRenderer
              state={state}
              scale={0.85}
              currentTime={currentTime}
              audioLevels={levels}
              isPlaying={isPlaying}
            />
          </Box>
        </Box>

        {/* Audio player controls */}
        {audioUrl && isReady && (
          <Box
            sx={{
              mt: 2,
              width: PREVIEW_WIDTHS[state.aspectRatio],
              maxWidth: "100%",
              backgroundColor: EDITOR_TOKENS.panelSurface,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "divider",
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <IconButton
              onClick={handlePlayPause}
              size="small"
              aria-label={isPlaying ? "Pause" : "Play"}
              sx={{
                backgroundColor: EDITOR_TOKENS.accent,
                color: "#fff",
                width: 32,
                height: 32,
                "&:hover": { backgroundColor: EDITOR_TOKENS.accentDark },
                "&:focus-visible": {
                  outline: `2px solid ${EDITOR_TOKENS.accentDark}`,
                  outlineOffset: "2px",
                },
              }}
            >
              {isPlaying ? (
                <PauseIcon sx={{ fontSize: 18 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>

            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "text.secondary",
                minWidth: 36,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmt(Math.max(elapsed, 0))}
            </Typography>

            <Slider
              value={currentTime}
              min={clipStart}
              max={clipEnd || 1}
              step={0.1}
              aria-label="Seek"
              onChange={(_, v) => setCurrentTime(v as number)}
              onChangeCommitted={handleSeekCommit}
              size="small"
              sx={{
                flex: 1,
                color: EDITOR_TOKENS.accent,
                height: 4,
                "& .MuiSlider-thumb": {
                  width: 12,
                  height: 12,
                },
                "& .MuiSlider-rail": {
                  opacity: 0.2,
                },
              }}
            />

            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "text.secondary",
                minWidth: 36,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmt(clipDuration)}
            </Typography>
          </Box>
        )}

        {/* No-audio empty state */}
        {!audioUrl && (
          <Box
            sx={{
              mt: 2,
              width: PREVIEW_WIDTHS[state.aspectRatio],
              maxWidth: "100%",
              backgroundColor: EDITOR_TOKENS.panelSurface,
              borderRadius: "12px",
              border: "1px dashed",
              borderColor: "divider",
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <MusicOffIcon sx={{ fontSize: 20, color: "text.secondary" }} />
            <Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                No audio on this episode
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                Playback is unavailable; the waveform preview is decorative.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Hidden audio element */}
      {audioUrl && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={audioRef}
          src={audioUrl}
          crossOrigin="anonymous"
          preload="metadata"
          style={{ display: "none" }}
        />
      )}
    </Box>
  );
}
